import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import {
    Card,
    Select,
    Input,
    Button,
    Space,
    Row,
    Col,
    Alert,
    Typography,
    message,
    Table,
    Checkbox,
    Modal,
    Spin,
    Dropdown,
    InputNumber,
    Tooltip,
    DatePicker
} from 'antd';
import {
    SearchOutlined,
    PlusOutlined,
    SyncOutlined,
    EyeOutlined,
    EditOutlined,
    DeleteOutlined,
    UploadOutlined,
    FileOutlined,
    MoreOutlined,
    PauseOutlined,
    FilterOutlined,
    ClearOutlined,
    CaretUpOutlined,
    CaretDownOutlined
} from '@ant-design/icons';
import { useTheme } from '../../context/ThemeContext';
import { API_BASE_URL } from '../../utils/config';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import isBetween from 'dayjs/plugin/isBetween';
import FormikForm from '../form/FormikForm';
import { getNumericFilters, getStringFilters, getDateFilters, formatCellValue , applyAdvancedFilter, getConfirmModalProps } from "../../utils/tableFilters";
import { trIncludes } from '../../utils/trText';

dayjs.extend(customParseFormat);
dayjs.extend(isBetween);

const { Title, Text } = Typography;
const { Option } = Select;

const iconMap = {
    'EyeOutlined': EyeOutlined,
    'EditOutlined': EditOutlined,
    'DeleteOutlined': DeleteOutlined,
    'PlusOutlined': PlusOutlined,
    'UploadOutlined': UploadOutlined,
    'FileOutlined': FileOutlined,
    'MoreOutlined': MoreOutlined,
    'SearchOutlined': SearchOutlined,
    'SyncOutlined': SyncOutlined,
    'PauseOutlined': PauseOutlined
};

const AracDetayPage = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const { darkMode } = useTheme();
    const { page, id } = useParams();
    const [selectedVehicleId, setSelectedVehicleId] = useState(id ? parseInt(id) : 0);
    const [selectedPage, setSelectedPage] = useState(page || 'araclar');
    const [vehicles, setVehicles] = useState([]);
    const [filteredVehicles, setFilteredVehicles] = useState([]);
    const [selectedVehicle, setSelectedVehicle] = useState(null);
    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [formModalOpen, setFormModalOpen] = useState(false);
    const [currentFormId, setCurrentFormId] = useState('');
    const [modalTitle, setModalTitle] = useState('');
    const [formTableName, setFormTableName] = useState('');
    const [formUniqueField, setFormUniqueField] = useState(null);
    const [currentUniqueValue, setCurrentUniqueValue] = useState(null);
    const [detailModalOpen, setDetailModalOpen] = useState(false);
    const [detailData, setDetailData] = useState([]);
    const [detailLoading, setDetailLoading] = useState(false);
    const [detailError, setDetailError] = useState(null);
    const [currentRecordId, setCurrentRecordId] = useState(null);
    const [modalButtonType, setModalButtonType] = useState('main-table');
    const [selectedLastiks, setSelectedLastiks] = useState([]);
    const [lastikOperationModal, setLastikOperationModal] = useState(false);
    const [lastikOperationType, setLastikOperationType] = useState(null);
    const [tireDetailModal, setTireDetailModal] = useState(false);
    const [tireDetailData, setTireDetailData] = useState([]);
    const [tireDetailLoading, setTireDetailLoading] = useState(false);
    const [tireDetailError, setTireDetailError] = useState(null);
    const [currentTireId, setCurrentTireId] = useState(null);   
    const [filteredInfo, setFilteredInfo] = useState({});
    const [sortedInfo, setSortedInfo] = useState({});
    const [columns, setColumns] = useState([]);
    const [showData, setShowData] = useState([]);
    const [searchTerm, setSearchTerm] = useState('');
    const loadVehiclesRef = useRef(null);

    const confirmDelete = (tableName, idField, record, successMessage) => {
        const id = record[idField] ?? record.id;
        Modal.confirm({
            ...getConfirmModalProps('Silme Onayı', 'Bu kaydı silmek istediğinize emin misiniz?', darkMode),
            onOk: async () => {
                try {
                    const res = await fetch(`${API_BASE_URL}/list/generic/update-active`, {
                        method: 'PUT',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({ table_name: tableName, id, active: 0 }),
                    });
                    if (!res.ok) throw new Error(`HTTP error! status: ${res.status}`);
                    message.success(successMessage);
                    loadVehiclesRef.current?.();
                } catch (err) {
                    console.error('Silme işlemi sırasında hata:', err);
                    message.error('Silme işlemi başarısız: ' + err.message);
                }
            },
        });
    };

    const confirmPassive = (tableName, idField, record, successMessage) => {
        const id = record[idField] ?? record.id;
        Modal.confirm({
            ...getConfirmModalProps('Pasif Onayı', 'Bu kaydı pasif hale getirmek istediğinize emin misiniz?', darkMode),
            onOk: async () => {
                try {
                    const res = await fetch(`${API_BASE_URL}/list/passive/generic-update-passive`, {
                        method: 'PUT',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({ table_name: tableName, id, pasif: 1 }),
                    });
                    if (!res.ok) throw new Error(`HTTP error! status: ${res.status}`);
                    message.success(successMessage);
                    loadVehiclesRef.current?.();
                } catch (err) {
                    console.error('Pasif işlemi sırasında hata:', err);
                    message.error('Pasif işlemi başarısız: ' + err.message);
                }
            },
        });
    };

    const openFormForRecord = (record, idField, formId, title, tableName) => {
        const id = record[idField];
        if (id) {
            handleOpenForm(formId, title, tableName, 'id', id);
        } else {
            message.error('ID bulunamadı');
        }
    };

    const vehicleTabs = [
        {
            key: 'araclar',
            label: 'Araç',
            defaultOpen: selectedPage === 'araclar'
        },
        {
            key: 'arac-lastik',
            label: 'Lastik',
            defaultOpen: selectedPage === 'arac-lastik',
            actionButtons: {
                lastColumn: [
                    {
                        key: 'edit',
                        label: 'Düzenle',
                        type: 'default',
                        icon: 'EditOutlined',
                        onClick: (record) => openFormForRecord(record, 'key_lastik_liste', 132, 'Araç Lastik', 'arac_lastik')
                    },
                    {
                        key: 'detail',
                        label: 'Detay',
                        type: 'default',
                        icon: 'EyeOutlined',
                        onClick: (record) => {
                            const id = record.key_lastik_liste;
                            if (id) handleOpenTireDetailModal(id);
                            else message.error('ID bulunamadı');
                        }
                    }
                ]
            },
            bulkActions: [
                {
                    key: 'depo-bulk',
                    label: 'Depoya Taşı',
                    type: 'default',
                    icon: 'SyncOutlined',
                    onClick: () => handleLastikOperation('depo')
                },
                {
                    key: 'hurda-bulk',
                    label: 'Hurdaya Taşı',
                    type: 'danger',
                    icon: 'DeleteOutlined',
                    onClick: () => handleLastikOperation('hurda')
                }
            ]
        },
        {
            key: 'arac-kaza',
            label: 'Hasar',
            actionButtons: {
                lastColumn: [
                    {
                        key: 'edit',
                        label: 'Düzenle',
                        type: 'default',
                        icon: 'EditOutlined',
                        onClick: (record) => openFormForRecord(record, 'key_kaza_liste', 139, 'Araç Kaza', 'arac_kaza')
                    },
                    {
                        key: 'delete',
                        label: 'Sil',
                        type: 'default',
                        icon: 'DeleteOutlined',
                        onClick: (record) => confirmDelete('arac_kaza', 'key_kaza_liste', record, 'Hasar kaydı başarıyla silindi')
                    },
                    {
                        key: 'upload',
                        label: 'Dosya Yükle',
                        type: 'default',
                        icon: 'UploadOutlined',
                        onClick: (record) => openFormForRecord(record, 'key_kaza_liste', 115, 'Araç Kaza Dosya', 'arac_kaza')
                    }
                ]
            }
        },
        {
            key: 'arac-ceza',
            label: 'Ceza',
            actionButtons: {
                lastColumn: [
                    {
                        key: 'edit',
                        label: 'Düzenle',
                        type: 'default',
                        icon: 'EditOutlined',
                        onClick: (record) => openFormForRecord(record, 'key_ceza_liste', 141, 'Araç Ceza', 'arac_ceza')
                    },
                    {
                        key: 'delete',
                        label: 'Sil',
                        type: 'default',
                        icon: 'DeleteOutlined',
                        onClick: (record) => confirmDelete('arac_ceza', 'key_ceza_liste', record, 'Ceza kaydı başarıyla silindi')
                    },
                    {
                        key: 'upload',
                        label: 'Dosya Yükle',
                        type: 'default',
                        icon: 'UploadOutlined',
                        onClick: (record) => openFormForRecord(record, 'key_ceza_liste', 117, 'Araç Ceza Dosya', 'arac_ceza')
                    },
                    {
                        key: 'viewFiles',
                        label: 'Dosyayı Aç',
                        type: 'default',
                        icon: 'EyeOutlined',
                        onClick: (record) => handleOpenDetailModal(record, null, 'sub-table', 'arac-ceza')
                    }
                ]
            }
        },
        {
            key: 'arac-ariza',
            label: 'Arıza',
            actionButtons: {
                lastColumn: [
                    {
                        key: 'edit',
                        label: 'Düzenle',
                        type: 'default',
                        icon: 'EditOutlined',
                        onClick: (record) => openFormForRecord(record, 'key_ariza_liste', 142, 'Araç Arıza', 'arac_ariza')
                    },
                    {
                        key: 'delete',
                        label: 'Sil',
                        type: 'default',
                        icon: 'DeleteOutlined',
                        onClick: (record) => confirmDelete('arac_ariza', 'key_ariza_liste', record, 'Arıza kaydı başarıyla silindi')
                    },
                    {
                        key: 'upload',
                        label: 'Dosya Yükle',
                        type: 'default',
                        icon: 'UploadOutlined',
                        onClick: (record) => openFormForRecord(record, 'key_ariza_liste', 119, 'Araç Arıza Dosya', 'arac_ariza')
                    },
                    {
                        key: 'viewFiles',
                        label: 'Dosyayı Aç',
                        type: 'default',
                        icon: 'EyeOutlined',
                        onClick: (record) => handleOpenDetailModal(record, null, 'sub-table', 'arac-ariza')
                    }
                ]
            }
        },
        {
            key: 'arac-sigorta',
            label: 'Sigorta',
            actionButtons: {
                lastColumn: [
                    {
                        key: 'edit',
                        label: 'Düzenle',
                        type: 'default',
                        icon: 'EditOutlined',
                        onClick: (record) => openFormForRecord(record, 'key_sigorta_liste', 145, 'Araç Sigorta', 'arac_sigorta')
                    },
                    {
                        key: 'delete',
                        label: 'Sil',
                        type: 'default',
                        icon: 'DeleteOutlined',
                        onClick: (record) => confirmDelete('arac_sigorta', 'key_sigorta_liste', record, 'Sigorta kaydı başarıyla silindi')
                    },
                    {
                        key: 'passive',
                        label: 'Pasif',
                        type: 'default',
                        icon: 'PauseOutlined',
                        onClick: (record) => confirmPassive('arac_sigorta', 'key_sigorta_liste', record, 'Sigorta kaydı başarıyla pasif hale getirildi.')
                    },
                    {
                        key: 'upload',
                        label: 'Dosya Yükle',
                        type: 'default',
                        icon: 'UploadOutlined',
                        onClick: (record) => openFormForRecord(record, 'key_sigorta_liste', 107, 'Araç Sigorta Dosya', 'arac_sigorta')
                    },
                    {
                        key: 'viewFiles',
                        label: 'Dosyayı Aç',
                        type: 'default',
                        icon: 'EyeOutlined',
                        onClick: (record) => handleOpenDetailModal(record, null, 'sub-table', 'arac-sigorta')
                    }
                ]
            }
        },
        {
            key: 'arac-muayene',
            label: 'Muayene',
            actionButtons: {
                lastColumn: [
                    {
                        key: 'edit',
                        label: 'Düzenle',
                        type: 'default',
                        icon: 'EditOutlined',
                        onClick: (record) => openFormForRecord(record, 'key_muayene_liste', 143, 'Araç Muayene', 'arac_muayene')
                    },
                    {
                        key: 'delete',
                        label: 'Sil',
                        type: 'default',
                        icon: 'DeleteOutlined',
                        onClick: (record) => confirmDelete('arac_muayene', 'key_muayene_liste', record, 'Muayene kaydı başarıyla silindi')
                    },
                    {
                        key: 'passive',
                        label: 'Pasif',
                        type: 'default',
                        icon: 'PauseOutlined',
                        onClick: (record) => confirmPassive('arac_muayene', 'key_muayene_liste', record, 'Muayene kaydı başarıyla pasif hale getirildi.')
                    },
                    {
                        key: 'upload',
                        label: 'Dosya Yükle',
                        type: 'default',
                        icon: 'UploadOutlined',
                        onClick: (record) => {
                            const id = record.key_muayene_liste;
                            if (id) {
                                handleOpenForm(113, 'Araç Muayene Dosya', 'arac_muayene', 'id', id);
                            } else {
                                message.error('ID bulunamadı');
                            }
                        }
                    },
                    {
                        key: 'viewFiles',
                        label: 'Dosyayı Aç',
                        type: 'default',
                        icon: 'EyeOutlined',
                        onClick: (record) => {
                            handleOpenDetailModal(record, null, 'sub-table', 'arac-muayene');
                        }
                    }
                ]
            }
        },
        {
            key: 'arac-bakim',
            label: 'Bakım',
            actionButtons: {
                lastColumn: [
                    {
                        key: 'edit',
                        label: 'Düzenle',
                        type: 'default',
                        icon: 'EditOutlined',
                        onClick: (record) => openFormForRecord(record, 'key_bakim_liste', 136, 'Araç Bakım', 'arac_bakim')
                    },
                    {
                        key: 'delete',
                        label: 'Sil',
                        type: 'default',
                        icon: 'DeleteOutlined',
                        onClick: (record) => confirmDelete('arac_bakim', 'key_bakim_liste', record, 'Bakım kaydı başarıyla silindi')
                    },
                    {
                        key: 'upload',
                        label: 'Dosya Yükle',
                        type: 'default',
                        icon: 'UploadOutlined',
                        onClick: (record) => openFormForRecord(record, 'key_bakim_liste', 111, 'Araç Bakım Dosya', 'arac_bakim')
                    },
                    {
                        key: 'viewFiles',
                        label: 'Dosyayı Aç',
                        type: 'default',
                        icon: 'EyeOutlined',
                        onClick: (record) => handleOpenDetailModal(record, null, 'sub-table', 'arac-bakim')
                    }
                ]
            }
        },
        {
            key: 'arac-aksesuar',
            label: 'Aksesuar',
            actionButtons: {
                lastColumn: [
                    {
                        key: 'edit',
                        label: 'Düzenle',
                        type: 'default',
                        icon: 'EditOutlined',
                        onClick: (record) => openFormForRecord(record, 'key_aksesuar_liste', 138, 'Araç Aksesuar', 'arac_aksesuar')
                    },
                    {
                        key: 'delete',
                        label: 'Sil',
                        type: 'default',
                        icon: 'DeleteOutlined',
                        onClick: (record) => confirmDelete('arac_aksesuar', 'key_aksesuar_liste', record, 'Aksesuar kaydı başarıyla silindi')
                    },
                    {
                        key: 'upload',
                        label: 'Dosya Yükle',
                        type: 'default',
                        icon: 'UploadOutlined',
                        onClick: (record) => openFormForRecord(record, 'key_aksesuar_liste', 129, 'Araç Aksesuar Dosya', 'arac_aksesuar')
                    },
                    {
                        key: 'viewFiles',
                        label: 'Dosyayı Aç',
                        type: 'default',
                        icon: 'EyeOutlined',
                        onClick: (record) => handleOpenDetailModal(record, null, 'sub-table', 'arac-aksesuar')
                    }
                ]
            }
        },
        {
            key: 'arac-masraf',
            label: 'Masraf'
        }
    ];

    useEffect(() => {
        if (darkMode) {
            document.body.setAttribute('data-theme', 'dark');
        } else {
            document.body.removeAttribute('data-theme');
        }
    }, [darkMode]);

    const loadVehicles = useCallback(async () => {
        try {
            const urlParams = new URLSearchParams(location.search);
            const source = urlParams.get('source');
            let endpoint = 'araclar-liste';

            if (source === 'sold') {
                endpoint = 'araclar-satilan';
            }

            const response = await fetch(`${API_BASE_URL}/list/execute/${endpoint}`, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                },
            });

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            const vehicleData = await response.json();
            setVehicles(vehicleData || []);
            setFilteredVehicles(vehicleData || []);

            if (selectedVehicleId && selectedVehicleId !== 0) {
                const foundVehicle = vehicleData.find(v => v.key_araclar === selectedVehicleId);
                if (foundVehicle) {
                    setSelectedVehicle(foundVehicle);
                } else {
                    setSelectedVehicle(null);
                }
            }

            if (vehicleData && vehicleData.length > 0 && selectedVehicleId === 0) {
                const firstVehicle = vehicleData[0];
                setSelectedVehicleId(firstVehicle.key_araclar);
                setSelectedVehicle(firstVehicle);
                navigate(`/arac-detay/${selectedPage}/${firstVehicle.key_araclar}`);
            }
        } catch (err) {
            console.error('Vehicle list load error:', err);
            message.error('Araç listesi yüklenirken hata oluştu: ' + err.message);
        }
    }, [selectedVehicleId, selectedPage, navigate, location.search]);

    useEffect(() => {
        loadVehiclesRef.current = loadVehicles;
    }, [loadVehicles]);

    const loadVehicleDetails = useCallback(async () => {
        if (!selectedVehicleId || selectedVehicleId === 0) {
            setData([]);
            setSelectedVehicle(null);
            return;
        }

        setLoading(true);
        setError(null);

        try {
            const vehicle = vehicles.find(v => v.key_araclar === selectedVehicleId);
            if (vehicle) {
                setSelectedVehicle(vehicle);
            }

            if (selectedPage === 'araclar') {
                setData([]);
                setLoading(false);
                return;
            }

            const pageQueryMap = {
                'arac-lastik': 'arac-lastik-liste',
                'arac-kaza': 'arac-kaza-liste',
                'arac-ceza': 'arac-ceza-liste',
                'arac-ariza': 'arac-ariza-liste',
                'arac-sigorta': 'arac-sigorta-liste',
                'arac-muayene': 'arac-muayene-liste',
                'arac-bakim': 'arac-bakim-liste',
                'arac-aksesuar': 'arac-aksesuar-liste',
                'arac-masraf': 'arac-masraf-liste',
            };
            const queryName = pageQueryMap[selectedPage] ?? 'zimmet-detail-a';

            let vehicleId = selectedVehicleId;
            if (selectedVehicleId === -2 || selectedVehicleId === -1) {
                vehicleId = Math.abs(selectedVehicleId);
            }

            const response = await fetch(`${API_BASE_URL}/list/execute-query/${queryName}`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({ params: { id: vehicleId } })
            });

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            const result = await response.json();
            const resultData = Array.isArray(result) ? result : [];
            setData(resultData);
            setShowData(resultData);

            if (resultData.length > 0) {
                const sample = resultData[0];
                const dynamicCols = Object.keys(sample)
                    .filter((key) => !key.startsWith('key_') && key !== 'arac_id')
                    .map((key) => {
                        const firstValue = sample[key];
                        const isDateColumn = key.toLowerCase().includes('tarih') || key.toLowerCase().includes('tar');
                        const isNumber = typeof firstValue === "number" && !isNaN(firstValue);
                        const uniqueValues = [...new Set(resultData.map((item) => item[key]))].filter(
                            (val) => val !== undefined && val !== null && val !== ""
                        );

                        let filters = [];
                        if (isNumber) {
                            filters = getNumericFilters();
                        } else if (isDateColumn) {
                            filters = getDateFilters();
                        } else {
                            filters = getStringFilters();
                        }

                        return {
                            key: key,
                            title: key,
                            dataIndex: key,
                            isDateColumn,
                            isNumber,
                            isString: !isNumber && !isDateColumn,
                            uniqueValues,
                            sortable: true,
                            filters, 
                        };
                    });
                setColumns(dynamicCols);
            }
        } catch (err) {
            console.error('Vehicle details load error:', err);
            setError('Araç detayları yüklenirken hata oluştu: ' + err.message);
        } finally {
            setLoading(false);
        }
    }, [selectedVehicleId, selectedPage, vehicles]);

    const handleVehicleSelect = (vehicleId) => {
        setSelectedVehicleId(vehicleId);
        navigate(`/arac-detay/${selectedPage}/${vehicleId}`);
    };

    const handleTabChange = (tabKey) => {
        setSelectedPage(tabKey);
        navigate(`/arac-detay/${tabKey}/${selectedVehicleId}`);
    };

    const handleOpenForm = (formId, title = 'Form', tableName = '', uniqueField = '', uniqueValue = null) => {
        setModalTitle(title);
        setCurrentFormId(formId);
        setFormTableName(tableName);
        setFormUniqueField(uniqueField);
        setCurrentUniqueValue(uniqueValue);
        setFormModalOpen(true);
    };

    const handleFormSuccess = () => {
        setFormModalOpen(false);
        setCurrentFormId(null);
        setFormTableName('');
        setFormUniqueField('');
        loadVehicleDetails();
    };

    const closeFormModal = () => {
        setFormModalOpen(false);
    };

    const handleOpenTireDetailModal = async (tireId) => {
        setCurrentTireId(tireId);
        setTireDetailModal(true);
        setTireDetailLoading(true);
        setTireDetailError(null);
        setTireDetailData([]);

        try {
            const response = await fetch(`${API_BASE_URL}/list/lastik/arac-lastik-detail`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({ id: tireId })
            });

            const result = await response.json();
            if (result.success) {
                setTireDetailData(result.data || []);
            } else {
                setTireDetailError(result.message || 'Lastik detayları yüklenemedi');
            }
        } catch (error) {
            console.error('Lastik detay hatası:', error);
            setTireDetailError('Lastik detayları yüklenirken hata oluştu');
        } finally {
            setTireDetailLoading(false);
        }
    };

    const closeTireDetailModal = () => {
        setTireDetailModal(false);
        setTireDetailData([]);
        setTireDetailError(null);
        setCurrentTireId(null);
    };

    const handleLastikSelection = (selectedRowKeys, selectedRows) => {
        setSelectedLastiks(selectedRows || []);
    };
    
    const selectedRowKeys = useMemo(() => {
        if (selectedPage !== 'arac-lastik') {
            return [];
        }
        return Array.isArray(selectedLastiks) 
            ? selectedLastiks
                .map(l => l?.key_lastik_liste)
                .filter(key => key !== undefined && key !== null && key !== '')
            : [];
    }, [selectedPage, selectedLastiks]);

    const rowSelection = useMemo(() => {
        if (selectedPage !== 'arac-lastik') {
            return undefined;
        }
        return {
            selectedRowKeys: selectedRowKeys,
            onChange: handleLastikSelection,
        };
    }, [selectedPage, selectedRowKeys, handleLastikSelection]);

    const handleLastikOperation = (operationType) => {
        if (selectedLastiks.length === 0) {
            message.warning('Lütfen en az bir lastik seçin');
            return;
        }
        setLastikOperationType(operationType);
        setLastikOperationModal(true);
    };

    const executeLastikOperation = async (operationData) => {
        try {
            const lastikIds = selectedLastiks.map(lastik => lastik.key_lastik_liste);

            const response = await fetch(`${API_BASE_URL}/list/lastik-operation`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    operation_type: lastikOperationType,
                    lastik_ids: lastikIds,
                    ...operationData
                })
            });

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            message.success('Lastik işlemi başarıyla tamamlandı');
            setLastikOperationModal(false);
            setSelectedLastiks([]);
            loadVehicleDetails();
        } catch (error) {
            console.error('Lastik işlemi hatası:', error);
            message.error('Lastik işlemi başarısız: ' + error.message);
        }
    };

    const handleOpenDetailModal = async (record, firstChar = null, modalType = 'main-table', fileType = null) => {
        const id = record.key_demirbas_zimmet || record.id || record.No || record.key_aracsozlesme_liste || record.key_kaza_liste || record.key_ceza_liste || record.key_sigorta_liste || record.key_muayene_liste || record.key_bakim_liste || record.key_sigorta_liste || record.key_ariza_liste || record.key_ceza_liste || record.key_kaza_liste || record.key_muayene_liste || record.key_bakim_liste || record.key_sigorta_liste || record.key_aksesuar_liste;
        if (!id) {
            message.error('ID bulunamadı');
            return;
        }

        const idString = String(id);
        setCurrentRecordId(idString);
        setDetailModalOpen(true);
        setDetailLoading(true);
        setDetailError(null);
        setModalButtonType(modalType);

        const charToUse = firstChar || idString.charAt(0).toLowerCase();
        let queryName = 'demirbas';
        let modalTitle = 'Araç Detayları';

        if (modalType === 'sub-table') {
            if (fileType) {
                queryName = 'arac-dosyalar';
                const fileTypeLabels = {
                    'arac-ruhsat': 'Ruhsat',
                    'arac-ariza': 'Arıza',
                    'arac-bakim': 'Bakım',
                    'arac-sozlesme': 'Sözleşme',
                    'arac-ceza': 'Ceza',
                    'arac-kaza': 'Kaza',
                    'arac-lastik': 'Lastik',
                    'arac-muayene': 'Muayene',
                    'arac-sigorta': 'Sigorta',
                    'arac-tahsis': 'Tahsis',
                    'arac-vergi': 'Vergi',
                    'arac-yedek': 'Yedek',
                    'arac-aksesuar': 'Aksesuar'
                };
                modalTitle = `${fileTypeLabels[fileType] || fileType} Dosyaları`;
            } else {
                queryName = 'demirbas';
                modalTitle = 'Araç Detayları';
            }
        } else {
            switch (charToUse) {
                case 'k':
                    modalTitle = 'Kredi Kartı Zimmet Detayları';
                    queryName = 'zimmet-detail-k';
                    break;
                case 'a':
                    modalTitle = 'Araç Zimmet Detayları';
                    queryName = 'zimmet-detail-a';
                    break;
                case 'd':
                    modalTitle = 'Araç Zimmet Detayları';
                    queryName = 'zimmet-detail-d';
                    break;
                default:
                    queryName = 'demirbas';
                    modalTitle = 'Araç Detayları';
                    break;
            }
        }

        let paramId = idString;
        let requestParams = { id: paramId };

        if (modalType === 'sub-table' && queryName === 'demirbas') {
            paramId = record.demirbas_id || record.id || idString;
            requestParams = { id: paramId };
        } else if (modalType === 'sub-table' && fileType) {
            const cleanId = idString.replace(/^[akd]/, '');
            requestParams = { id: cleanId, dosya_tipi: fileType };
        }

        try {
            const response = await fetch(`${API_BASE_URL}/list/execute-query/${queryName}`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({ params: requestParams })
            });

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            const data = await response.json();
            setDetailData(Array.isArray(data) ? data : []);
            setModalTitle(modalTitle);
        } catch (err) {
            console.error('Detail modal data load error:', err);
            setDetailError('Detay verileri yüklenirken hata oluştu: ' + err.message);
        } finally {
            setDetailLoading(false);
        }
    };

    const closeDetailModal = () => {
        setDetailModalOpen(false);
        setDetailData([]);
        setDetailError(null);
        setCurrentRecordId(null);
        setModalButtonType('main-table');
    };

    const handleViewFile = (record) => {
        const filePath = record.file;
        if (filePath) {
            const fileUrl = `${API_BASE_URL}/files/${filePath}`;
            window.open(fileUrl, '_blank');
        } else {
            message.warning('Dosya bulunamadı');
        }
    };

    const handleSubEditFile = (record) => {
        const id = record.dosyaID;
        if (!id) {
            message.error('Dosya ID bulunamadı');
            return;
        }
        const fileType = record.root;
        const formIds = {
            'arac-ruhsat': 110,
            'arac-kaza': 116,
            'arac-bakim': 112,
            'arac-ceza': 118,
            'arac-ariza': 120,
            'arac-muayene': 108,
            'arac-sigorta': 114,
            'arac-sozlesme': 125,
            'arac-aksesuar': 130
        };

        const formId = formIds[fileType] || 109;
        const formTitle = `${fileType.replace('arac-', '').toUpperCase()} DOSYA DÜZENLE`;
        handleOpenForm(formId, formTitle, 'araclar_dosya', 'id', id);
    };

    const getVehicleDisplayName = () => {
        if (selectedVehicleId === -2) return 'Hurda';
        if (selectedVehicleId === -1) return 'Depo';
        if (selectedVehicleId === 0) return 'Araç Seçiniz';
        if (selectedVehicle) {
            const plateField = selectedVehicle.Plaka || selectedVehicle.plaka || selectedVehicle.plate || selectedVehicle.Plate;
            return plateField || 'Bilinmeyen Araç';
        }
        return 'Bilinmeyen Araç';
    };

    const handleAdvancedFilterChange = (columnKey, valueType, value) => {
        setFilteredInfo((prev) => ({
            ...prev,
            [columnKey]: {
                ...prev[columnKey],
                [valueType]: value,
            },
        }));
    };

    const clearColumnFilter = (columnKey) => {
        setFilteredInfo((prev) => {
            const newFilteredInfo = { ...prev };
            delete newFilteredInfo[columnKey];
            return newFilteredInfo;
        });
    };

    const clearAllFilters = useCallback(() => {
        setFilteredInfo({});
        setSortedInfo({});
        setSearchTerm('');
    }, []);

    const handleSort = (columnKey) => {
        const column = columns.find((col) => col.key === columnKey);
        if (!column) return;

        let newOrder = "asc";
        if (sortedInfo.columnKey === columnKey) {
            if (sortedInfo.order === "asc") {
                newOrder = "desc";
            } else if (sortedInfo.order === "desc") {
                newOrder = null;
            }
        }

        setSortedInfo({
            columnKey: newOrder ? columnKey : null,
            order: newOrder,
        });
    };

    useEffect(() => {
        loadVehicles();
    }, [loadVehicles]);

    useEffect(() => {
        loadVehicleDetails();
    }, [loadVehicleDetails]);

    const filteredAndSortedData = useMemo(() => {
        let result = data.filter((item) => {
            const matchesSearch = Object.values(item).some((val) =>
                trIncludes(String(val), searchTerm)
            );

            const matchesFilters = Object.entries(filteredInfo).every(([key, filter]) => {
                const recordValue = item[key];
                const col = columns.find((c) => c.key === key);
                return applyAdvancedFilter(recordValue, filter, col);
            });

            return matchesSearch && matchesFilters;
        });

        if (sortedInfo.columnKey && sortedInfo.order) {
            const column = columns.find((col) => col.key === sortedInfo.columnKey);
            if (column) {
                result.sort((a, b) => {
                    const valA = a[sortedInfo.columnKey];
                    const valB = b[sortedInfo.columnKey];

                    if (column.isDateColumn) {
                        let dateA = null, dateB = null;
                        
                        if (valA) {
                            const dateValueA = String(valA).trim();
                            if (dateValueA === '0000-00-00' || dateValueA === '0000-00-00 00:00:00' || dateValueA === '1900-01-01' || dateValueA === '' || dateValueA === 'null' || dateValueA === 'undefined') {
                                dateA = null; 
                            } else if (dateValueA.match(/^\d{2}\.\d{2}\.\d{4}$/)) {
                                dateA = dayjs(dateValueA, 'DD.MM.YYYY');
                            } else if (dateValueA.match(/^\d{4}-\d{2}-\d{2}$/)) {
                                dateA = dayjs(dateValueA, 'YYYY-MM-DD');
                            } else if (dateValueA.match(/^\d{4}\.\d{2}\.\d{2}$/)) {
                                dateA = dayjs(dateValueA, 'YYYY.MM.DD');
                            } else if (dateValueA.match(/^\d{2}-\d{2}-\d{4}$/)) {
                                dateA = dayjs(dateValueA, 'DD-MM-YYYY');
                            } else if (dateValueA.match(/^\d{4}\/\d{2}\/\d{2}$/)) {
                                dateA = dayjs(dateValueA, 'YYYY/MM/DD');
                            } else if (dateValueA.match(/^\d{2}\/\d{2}\/\d{4}$/)) {
                                dateA = dayjs(dateValueA, 'DD/MM/YYYY');
                            } else {
                                dateA = dayjs(valA);
                            }
                        }
                        
                        if (valB) {
                            const dateValueB = String(valB).trim();
                            if (dateValueB === '0000-00-00' || dateValueB === '0000-00-00 00:00:00' || dateValueB === '1900-01-01' || dateValueB === '' || dateValueB === 'null' || dateValueB === 'undefined') {
                                dateB = null; 
                            } else if (dateValueB.match(/^\d{2}\.\d{2}\.\d{4}$/)) {
                                dateB = dayjs(dateValueB, 'DD.MM.YYYY');
                            } else if (dateValueB.match(/^\d{4}-\d{2}-\d{2}$/)) {
                                dateB = dayjs(dateValueB, 'YYYY-MM-DD');
                            } else if (dateValueB.match(/^\d{4}\.\d{2}\.\d{2}$/)) {
                                dateB = dayjs(dateValueB, 'YYYY.MM.DD');
                            } else if (dateValueB.match(/^\d{2}-\d{2}-\d{4}$/)) {
                                dateB = dayjs(dateValueB, 'DD-MM-YYYY');
                            } else if (dateValueB.match(/^\d{4}\/\d{2}\/\d{2}$/)) {
                                dateB = dayjs(dateValueB, 'YYYY/MM/DD');
                            } else if (dateValueB.match(/^\d{2}\/\d{2}\/\d{4}$/)) {
                                dateB = dayjs(dateValueB, 'DD/MM/YYYY');
                            } else {
                                dateB = dayjs(valB);
                            }
                        }
                        
                        if (!dateA && !dateB) return 0;
                        if (!dateA || !dateA.isValid()) return sortedInfo.order === "asc" ? -1 : 1;
                        if (!dateB || !dateB.isValid()) return sortedInfo.order === "asc" ? 1 : -1;
                        const comparison = dateA.isBefore(dateB) ? -1 : dateA.isAfter(dateB) ? 1 : 0;
                        return sortedInfo.order === "asc" ? comparison : -comparison;
                    } else if (column.isNumber) {
                        const numA = Number(valA) || 0;
                        const numB = Number(valB) || 0;
                        return sortedInfo.order === "asc" ? numA - numB : numB - numA;
                    } else {
                        const strA = String(valA || "").toLowerCase();
                        const strB = String(valB || "").toLowerCase();
                        const comparison = strA.localeCompare(strB, 'tr');
                        return sortedInfo.order === "asc" ? comparison : -comparison;
                    }
                });
            }
        }

        return result;
    }, [data, searchTerm, filteredInfo, sortedInfo, columns]);

    const getSortIcon = (columnKey) => {
        if (sortedInfo.columnKey === columnKey) {
            return sortedInfo.order === "asc" ? <CaretUpOutlined /> : <CaretDownOutlined />;
        }
        return null;
    };

    const removeColumn = (columnKey) => {
        setColumns((prevCols) => prevCols.filter((col) => col.key !== columnKey));
    };

    const getFilterDropdown = (column) => {
        const filter = filteredInfo[column.key] || {};
        const isActive = filter.condition && filter.condition !== "choose";

        return (
            <div style={{ padding: 8, minWidth: 200 }}>
                <Select
                    placeholder="Filtre Seçin"
                    value={filter.condition || "choose"}
                    onChange={(value) => handleAdvancedFilterChange(column.key, "condition", value)}
                    style={{ width: "100%", marginBottom: 8 }}
                    className={darkMode ? 'dark-select' : ''}
                >
                    {column.filters.map((filter) => (
                        <Option key={filter.value} value={filter.value}>
                            {filter.text}
                        </Option>
                    ))}
                </Select>

                {filter.condition && filter.condition !== "choose" && (
                    <>
                        {column.isNumber && filter.condition === "between" ? (
                            <div style={{ display: "flex", gap: 4, marginBottom: 8 }}>
                                <InputNumber
                                    placeholder="Min"
                                    value={filter.min}
                                    onChange={(value) => handleAdvancedFilterChange(column.key, "min", value)}
                                    style={{ flex: 1 }}
                                    className={darkMode ? 'dark-input' : ''}
                                />
                                <InputNumber
                                    placeholder="Max"
                                    value={filter.max}
                                    onChange={(value) => handleAdvancedFilterChange(column.key, "max", value)}
                                    style={{ flex: 1 }}
                                    className={darkMode ? 'dark-input' : ''}
                                />
                            </div>
                        ) : column.isDateColumn && filter.condition === "between" ? (
                            <div style={{ display: "flex", gap: 4, marginBottom: 8 }}>
                                <DatePicker
                                    placeholder="Başlangıç"
                                    value={filter.startDate ? dayjs(filter.startDate, 'DD.MM.YYYY') : null}
                                    onChange={(date) => handleAdvancedFilterChange(column.key, "startDate", date)}
                                    format="DD.MM.YYYY"
                                    style={{ flex: 1 }}
                                    className={darkMode ? 'dark-date-picker' : ''}
                                />
                                <DatePicker
                                    placeholder="Bitiş"
                                    value={filter.endDate ? dayjs(filter.endDate, 'DD.MM.YYYY') : null}
                                    onChange={(date) => handleAdvancedFilterChange(column.key, "endDate", date)}
                                    format="DD.MM.YYYY"
                                    style={{ flex: 1 }}
                                    className={darkMode ? 'dark-date-picker' : ''}
                                />
                            </div>
                        ) : column.isDateColumn && filter.condition !== "thisMonth" && filter.condition !== "thisYear" && filter.condition !== "lastMonth" && filter.condition !== "lastYear" ? (
                            <DatePicker
                                placeholder="Tarih Seçin"
                                value={filter.value ? dayjs(filter.value, 'DD.MM.YYYY') : null}
                                onChange={(date) => handleAdvancedFilterChange(column.key, "value", date)}
                                format="DD.MM.YYYY"
                                style={{ width: "100%", marginBottom: 8 }}
                                className={darkMode ? 'dark-date-picker' : ''}
                            />
                        ) : filter.condition === "select" ? (
                            <Select
                                placeholder="Değer Seçin"
                                value={filter.value}
                                onChange={(value) => handleAdvancedFilterChange(column.key, "value", value)}
                                style={{ width: "100%", marginBottom: 8 }}
                                className={darkMode ? 'dark-select' : ''}
                            >
                                {column.uniqueValues.map((val) => (
                                    <Option key={val} value={val}>
                                        {val}
                                    </Option>
                                ))}
                            </Select>
                        ) : !["empty", "notEmpty", "thisMonth", "thisYear", "lastMonth", "lastYear"].includes(filter.condition) ? (
                            <Input
                                placeholder="Değer Girin"
                                value={filter.value || ""}
                                onChange={(e) => handleAdvancedFilterChange(column.key, "value", e.target.value)}
                                style={{ width: "100%", marginBottom: 8 }}
                                className={darkMode ? 'dark-input' : ''}
                            />
                        ) : null}

                        <div style={{ display: "flex", gap: 4 }}>
                            <Button
                                type="primary"
                                size="small"
                                onClick={() => {
                                    setFilteredInfo((prev) => ({
                                        ...prev,
                                        [column.key]: { ...filter, applied: true },
                                    }));
                                }}
                                style={{ flex: 1 }}
                                className={darkMode ? 'dark-button' : ''}
                            >
                                Uygula
                            </Button>
                            <Button
                                size="small"
                                onClick={() => clearColumnFilter(column.key)}
                                style={{ flex: 1 }}
                                className={darkMode ? 'dark-button' : ''}
                            >
                                Temizle
                            </Button>
                        </div>
                    </>
                )}
                        </div>
                    );
    };

    const handleCheckboxChange = useCallback((record, checked) => {
        setSelectedLastiks(prev => {
            if (checked) {
                if (!prev.some(item => item.key_lastik_liste === record.key_lastik_liste)) {
                    return [...prev, record];
                }
                return prev;
            } else {
                return prev.filter(item => item.key_lastik_liste !== record.key_lastik_liste);
            }
        });
    }, []);
    
    const handleSelectAll = useCallback((checked) => {
        if (checked) {
            setSelectedLastiks(filteredAndSortedData);
        } else {
            setSelectedLastiks([]);
        }
    }, [filteredAndSortedData]);
    
    const isRowSelected = useCallback((record) => {
        return selectedLastiks.some(item => item.key_lastik_liste === record.key_lastik_liste);
    }, [selectedLastiks]);
    
    const isAllSelected = useMemo(() => {
        return filteredAndSortedData.length > 0 && 
               filteredAndSortedData.every(record => 
                   selectedLastiks.some(item => item.key_lastik_liste === record.key_lastik_liste)
               );
    }, [filteredAndSortedData, selectedLastiks]);
    
    const safeLastikColumns = useMemo(() => {
        if (selectedPage !== 'arac-lastik') {
            return [];
        }
        
        if (!Array.isArray(columns) || columns.length === 0) {
            return [];
        }
        
        const checkboxColumn = {
            key: '__selection__',
            dataIndex: '__selection__',
            width: 50,
            fixed: 'left',
            title: (
                <Checkbox
                    checked={isAllSelected}
                    indeterminate={selectedLastiks.length > 0 && !isAllSelected}
                    onChange={(e) => handleSelectAll(e.target.checked)}
                    className={darkMode ? 'dark-checkbox' : ''}
                />
            ),
            render: (_, record) => (
                <Checkbox
                    checked={isRowSelected(record)}
                    onChange={(e) => handleCheckboxChange(record, e.target.checked)}
                    className={darkMode ? 'dark-checkbox' : ''}
                />
            ),
        };
        
        const regularColumns = columns
            .filter(col => col && col.key && typeof col === 'object') 
            .map((column) => {
                const isActive = filteredInfo[column.key] && filteredInfo[column.key].condition && filteredInfo[column.key].condition !== "choose";
                
                return {
                    key: String(column.key),
                    dataIndex: column.dataIndex || column.key,
                    title: (
                        <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
                            <Tooltip title="Sütunu Kaldır">
                                <Button
                                    type="text"
                                    size="small"
                                    icon={<ClearOutlined />}
                                    onClick={() => removeColumn(column.key)}
                                    style={{ 
                                        color: darkMode ? 'var(--dark-text-muted)' : '#999',
                                        padding: '2px 4px',
                                        minWidth: 'auto',
                                        height: 'auto'
                                    }}
                                    className={darkMode ? 'dark-button' : ''}
                                />
                            </Tooltip>
                            <span style={{ flex: 1 }}>{column.title}</span>
                            {column.sortable && (
                                <Button
                                    type="text"
                                    size="small"
                                    icon={getSortIcon(column.key)}
                                    onClick={() => handleSort(column.key)}
                                    style={{ 
                                        color: darkMode ? 'var(--dark-text-muted)' : '#999',
                                        padding: '2px 4px',
                                        minWidth: 'auto',
                                        height: 'auto'
                                    }}
                                    className={darkMode ? 'dark-button' : ''}
                                />
                            )}
                            <Dropdown
                                overlay={getFilterDropdown(column)}
                                trigger={['click']}
                                placement="bottomRight"
                            >
                                <Button
                                    type="text"
                                    size="small"
                                    icon={<FilterOutlined />}
                                    style={{ 
                                        color: isActive ? '#1890ff' : (darkMode ? 'var(--dark-text-muted)' : '#999'),
                                        padding: '2px 4px',
                                        minWidth: 'auto',
                                        height: 'auto'
                                    }}
                                    className={darkMode ? 'dark-button' : ''}
                                />
                            </Dropdown>
                        </div>
                    ),
                    render: (text, record) => (
                        <span>{formatCellValue(column.key, text)}</span>
                    ),
                    ...(column.filters && { filters: column.filters }),
                    ...(column.sorter && { sorter: column.sorter }),
                    ...(column.width && { width: column.width }),
                    ...(column.fixed && { fixed: column.fixed }),
                    ...(column.sortable !== undefined && { sortable: column.sortable }),
                    ...(column.align && { align: column.align }),
                    ...(column.ellipsis !== undefined && { ellipsis: column.ellipsis }),
                };
            });
        
        return [checkboxColumn, ...regularColumns];
    }, [selectedPage, columns, filteredInfo, darkMode, sortedInfo, getFilterDropdown, getSortIcon, removeColumn, handleSort, formatCellValue, isAllSelected, selectedLastiks, handleSelectAll, isRowSelected, handleCheckboxChange]);

    return (
        <div className={`fatura-page ${darkMode ? 'dark-theme' : ''}`}>
            {/* Vehicle Selection Header */}
            <Card
                className={`fatura-filters-card ${darkMode ? 'dark-card' : ''}`}
                style={{
                    marginTop: 16,
                    backgroundColor: darkMode ? 'var(--dark-bg-secondary)' : undefined,
                    borderColor: darkMode ? 'var(--dark-border)' : undefined
                }}
            >
                <Row gutter={[16, 16]} align="middle">
                    <Col xs={24} sm={12} md={4} lg={3}>
                        <div className="filter-item">
                            <Text strong style={{ color: darkMode ? 'var(--dark-text)' : undefined }}>
                                Araç Seçimi
                            </Text>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                <Title level={4} style={{ margin: 0, color: darkMode ? 'var(--dark-text)' : undefined }}>
                                    {getVehicleDisplayName()}
                                </Title>
                            </div>
                        </div>
                    </Col>

                    <Col xs={24} sm={12} md={4} lg={3}>
                        <div className="filter-item">
                            <Text strong style={{ color: darkMode ? 'var(--dark-text)' : undefined }}>
                                Araç Listesi
                            </Text>
                            <Select
                                placeholder="Araç seçin"
                                value={selectedVehicleId}
                                onChange={handleVehicleSelect}
                                style={{ width: '100%' }}
                                className={darkMode ? 'dark-select' : ''}
                                showSearch
                                filterOption={(input, option) =>
                                    trIncludes(option?.children ?? '', input)
                                }
                                loading={loading}
                            >
                                <Option key={-2} value={-2}>Hurda</Option>
                                <Option key={-1} value={-1}>Depo</Option>
                                {filteredVehicles.map(vehicle => {
                                    const plateField = vehicle.Plaka || vehicle.plaka || vehicle.plate || vehicle.Plate || 'Bilinmeyen';
                                    return (
                                        <Option key={vehicle.key_araclar || vehicle.id} value={vehicle.key_araclar || vehicle.id}>
                                            {plateField}
                                        </Option>
                                    );
                                })}
                            </Select>
                        </div>
                    </Col>

                    <Col xs={24} sm={12} md={6} lg={6}>
                        <div className="filter-item">
                            <Text strong style={{ color: darkMode ? 'var(--dark-text)' : undefined }}>
                                Arama
                            </Text>
                            <Input
                                placeholder="Arama yapın..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                prefix={<SearchOutlined style={{ color: darkMode ? 'var(--dark-text-muted)' : undefined }} />}
                                allowClear
                                style={{
                                    backgroundColor: darkMode ? 'var(--dark-bg)' : undefined,
                                    borderColor: darkMode ? 'var(--dark-border)' : undefined,
                                    color: darkMode ? 'var(--dark-text)' : undefined
                                }}
                                className={darkMode ? 'dark-input' : ''}
                            />
                        </div>
                    </Col>

                    <Col xs={24} sm={12} md={10} lg={12}>
                        <div className="filter-actions" style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'end', height: '100%' }}>
                            <Space wrap>
                                <Button
                                    onClick={clearAllFilters}
                                    className={darkMode ? 'dark-button' : ''}
                                    style={{
                                        backgroundColor: darkMode ? 'var(--dark-bg-header)' : undefined,
                                        borderColor: darkMode ? 'var(--dark-border)' : undefined,
                                        color: darkMode ? 'var(--dark-text)' : undefined
                                    }}
                                >
                                    Temizle
                                </Button>
                                {selectedPage === 'arac-lastik' && (
                                    <Button
                                        key="yeni-lastik-ekle"
                                        type="primary"
                                        icon={<PlusOutlined />}
                                        onClick={() => {
                                            handleOpenForm(132, 'Araç Lastik', 'arac_lastik', 'id', '');
                                        }}
                                        loading={loading}
                                        className={darkMode ? 'dark-button' : ''}
                                    >
                                        Yeni Lastik Ekle
                                    </Button>
                                )}
                                <Button
                                    type="primary"
                                    icon={<PlusOutlined />}
                                    onClick={() => handleOpenForm(131, 'Araç Ekle', 'araclar', 'id', '')}
                                    className={darkMode ? 'dark-button' : ''}
                                >
                                    Yeni Araç Ekle
                                </Button>
                                <Button
                                    type="default"
                                    onClick={() => navigate('/araclar')}
                                    className={darkMode ? 'dark-button' : ''}
                                    style={{
                                        backgroundColor: darkMode ? 'var(--dark-bg-header)' : undefined,
                                        borderColor: darkMode ? 'var(--dark-border)' : undefined,
                                        color: darkMode ? 'var(--dark-text)' : undefined
                                    }}
                                >
                                    Araç Listesi
                                </Button>
                            </Space>
                        </div>
                    </Col>
                </Row>
            </Card>

            {/* Tab Navigation */}
            {selectedVehicleId !== 0 && (
                <Card
                    className={`fatura-content-card ${darkMode ? 'dark-card' : ''}`}
                    style={{
                        marginTop: 16,
                        backgroundColor: darkMode ? 'var(--dark-bg-secondary)' : undefined,
                        borderColor: darkMode ? 'var(--dark-border)' : undefined
                    }}
                >
                    <div className="tab-navigation" style={{ marginBottom: 16 }}>
                        <Space wrap>
                            {vehicleTabs.map(tab => (
                                <Button
                                    key={tab.key}
                                    type={selectedPage === tab.key ? 'primary' : 'default'}
                                    onClick={() => handleTabChange(tab.key)}
                                    className={darkMode ? 'dark-button' : ''}
                                    style={{
                                        backgroundColor: selectedPage === tab.key
                                            ? undefined
                                            : (darkMode ? 'var(--dark-bg-header)' : undefined),
                                        borderColor: darkMode ? 'var(--dark-border)' : undefined,
                                        color: darkMode ? 'var(--dark-text)' : undefined
                                    }}
                                >
                                    {tab.label}
                                </Button>
                            ))}
                        </Space>
                    </div>

                    {/* Data Table */}
                    {error && (
                        <Alert
                            message="Hata"
                            description={error}
                            type="error"
                            showIcon
                            style={{ marginBottom: 16 }}
                        />
                    )}

                    {/* Araç Tab - Show Form */}
                    {selectedPage === 'araclar' ? (
                        <div>
                            {selectedVehicleId === -2 || selectedVehicleId === -1 || selectedVehicleId === 0 ? (
                                <div style={{
                                    textAlign: 'center',
                                    padding: 40,
                                    color: darkMode ? 'var(--dark-text-muted)' : '#999'
                                }}>
                                    <Text style={{ fontSize: 16 }}>Lütfen Araç Seçiniz...</Text>
                                </div>
                            ) : (
                                <Card
                                    style={{
                                        backgroundColor: darkMode ? 'var(--dark-bg-secondary)' : undefined,
                                        borderColor: darkMode ? 'var(--dark-border)' : undefined
                                    }}
                                    className={darkMode ? 'dark-card' : ''}
                                >
                                    <div style={{ padding: 16 }}>
                                        <FormikForm
                                            formId={131}
                                            uniqueValue={selectedVehicleId}
                                            uniqueField="id"
                                            tableName="araclar"
                                            onSuccess={() => {
                                                message.success('Araç bilgileri güncellendi');
                                                loadVehicles();
                                            }}
                                        />
                                    </div>
                                </Card>
                            )}
                        </div>
                    ) : (
                        /* Other Tabs - Show Data Table */
                        data && data.length > 0 ? (
                            selectedPage === 'arac-lastik' ? (
                                <div>
                                    <div style={{ marginBottom: 16, display: 'flex', gap: 8 }}>
                                        {vehicleTabs.find(tab => tab.key === 'arac-lastik')?.bulkActions?.map(action => {
                                            const IconComponent = iconMap[action.icon];
                                            return (
                                                <Button
                                                    key={action.key}
                                                    type={action.type}
                                                    icon={IconComponent ? <IconComponent /> : null}
                                                    onClick={action.onClick}
                                                    className={darkMode ? 'dark-button' : ''}
                                                    style={{
                                                        backgroundColor: darkMode ? 'var(--dark-bg-header)' : undefined,
                                                        borderColor: darkMode ? 'var(--dark-border)' : undefined,
                                                        color: darkMode ? 'var(--dark-text)' : undefined
                                                    }}
                                                >
                                                    {action.label}
                                                </Button>
                                            );
                                        })}
                                    </div>

                                    <Table
                                        dataSource={filteredAndSortedData}
                                        loading={loading}
                                        rowKey={(record) => record.key_lastik_liste || Math.random()}
                                        scroll={{ x: 'max-content' }}
                                        className={darkMode ? 'dark-table' : ''}
                                        size="small"
                                        style={{
                                            width: '100%',
                                            backgroundColor: darkMode ? 'var(--dark-bg-secondary)' : undefined
                                        }}
                                        pagination={{
                                            pageSize: 10,
                                            showSizeChanger: true,
                                            showQuickJumper: true,
                                            showTotal: (total, range) =>
                                                `${range[0]}-${range[1]} / ${total} kayıt`,
                                            pageSizeOptions: ['10', '25', '50', '100']
                                        }}
                                        columns={safeLastikColumns}
                                    />
                                </div>
                            ) : (
                                <Table
                                    dataSource={filteredAndSortedData}
                                    loading={loading}
                                    rowKey={(record) => {
                                        return record.key_lastik_liste ||
                                            record.key_kaza_liste ||
                                            record.key_ceza_liste ||
                                            record.key_ariza_liste ||
                                            record.key_sigorta_liste ||
                                            record.key_muayene_liste ||
                                            record.key_bakim_liste ||
                                            record.key_aksesuar_liste ||
                                            record.key_masraf_liste ||
                                            Math.random();
                                    }}
                                    scroll={{ x: 'max-content' }}
                                    className={darkMode ? 'dark-table' : ''}
                                    size="small"
                                    style={{
                                        width: '100%',
                                        backgroundColor: darkMode ? 'var(--dark-bg-secondary)' : undefined
                                    }}
                                    pagination={{
                                        pageSize: 10,
                                        showSizeChanger: true,
                                        showQuickJumper: true,
                                        showTotal: (total, range) =>
                                            `${range[0]}-${range[1]} / ${total} kayıt`,
                                        pageSizeOptions: ['10', '25', '50', '100']
                                    }}
                                    columns={columns.map((column) => {
                                        const isActive = filteredInfo[column.key] && filteredInfo[column.key].condition && filteredInfo[column.key].condition !== "choose";
                                        
                                        return {
                                            ...column,
                                            title: (
                                                <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
                                                    <Tooltip title="Sütunu Kaldır">
                                                        <Button
                                                            type="text"
                                                            size="small"
                                                            icon={<ClearOutlined />}
                                                            onClick={() => removeColumn(column.key)}
                                                            style={{ 
                                                                color: darkMode ? 'var(--dark-text-muted)' : '#999',
                                                                padding: '2px 4px',
                                                                minWidth: 'auto',
                                                                height: 'auto'
                                                            }}
                                                            className={darkMode ? 'dark-button' : ''}
                                                        />
                                                    </Tooltip>
                                                    <span style={{ flex: 1 }}>{column.title}</span>
                                                    {column.sortable && (
                                                        <Button
                                                            type="text"
                                                            size="small"
                                                            icon={getSortIcon(column.key)}
                                                            onClick={() => handleSort(column.key)}
                                                            style={{ 
                                                                color: darkMode ? 'var(--dark-text-muted)' : '#999',
                                                                padding: '2px 4px',
                                                                minWidth: 'auto',
                                                                height: 'auto'
                                                            }}
                                                            className={darkMode ? 'dark-button' : ''}
                                                        />
                                                    )}
                                                    <Dropdown
                                                        overlay={getFilterDropdown(column)}
                                                        trigger={['click']}
                                                        placement="bottomRight"
                                                    >
                                                        <Button
                                                            type="text"
                                                            size="small"
                                                            icon={<FilterOutlined />}
                                                            style={{ 
                                                                color: isActive ? '#1890ff' : (darkMode ? 'var(--dark-text-muted)' : '#999'),
                                                                padding: '2px 4px',
                                                                minWidth: 'auto',
                                                                height: 'auto'
                                                            }}
                                                            className={darkMode ? 'dark-button' : ''}
                                                        />
                                                    </Dropdown>
                                                </div>
                                            ),
                                            render: (text, record) => (
                                                <span>{formatCellValue(column.key, text)}</span>
                                            )
                                        };
                                    })}
                                />
                            )
                        ) : (
                            <div style={{
                                textAlign: 'center',
                                padding: 40,
                                color: darkMode ? 'var(--dark-text-muted)' : '#999'
                            }}>
                                {loading ? 'Veriler yükleniyor...' : 'Gösterilecek veri bulunamadı.'}
                            </div>
                        )
                    )}
                </Card>
            )}

            {/* Lastik İşlem Modal */}
            {lastikOperationModal && (
                <Modal
                    title={`Lastik İşlemi - ${lastikOperationType === 'edit' ? 'Toplu Düzenle' : lastikOperationType === 'depo' ? 'Depoya Taşı' : 'Hurdaya Taşı'}`}
                    open={lastikOperationModal}
                    onCancel={() => setLastikOperationModal(false)}
                    footer={null}
                    destroyOnHidden={true}
                    width={800}
                    style={{ top: 20 }}
                >
                    <div style={{ maxHeight: 600, overflowY: 'auto' }}>
                        <div style={{ marginBottom: 16 }}>
                            <h4>Seçilen Lastikler:</h4>
                            <ul>
                                {selectedLastiks.map((lastik, index) => (
                                    <li key={index}>
                                        {lastik['Seri No']} - {lastik['Marka']} {lastik['Tip']}
                                    </li>
                                ))}
                            </ul>
                        </div>

                        {lastikOperationType === 'edit' && (
                            <div>
                                <p>Toplu düzenleme formu burada olacak...</p>
                                <Button
                                    type="primary"
                                    onClick={() => executeLastikOperation({})}
                                    style={{ marginTop: 16 }}
                                >
                                    Düzenle
                                </Button>
                            </div>
                        )}

                        {lastikOperationType === 'depo' && (
                            <div>
                                <p>Seçilen lastikler depoya taşınacak.</p>
                                <p><strong>Veritabanında değişiklik:</strong> arac_id = -1 (Depo)</p>
                                <Button
                                    type="primary"
                                    onClick={() => executeLastikOperation({})}
                                    style={{ marginTop: 16 }}
                                >
                                    Depoya Taşı
                                </Button>
                            </div>
                        )}

                        {lastikOperationType === 'hurda' && (
                            <div>
                                <p>Seçilen lastikler hurdaya taşınacak.</p>
                                <p><strong>Veritabanında değişiklik:</strong> arac_id = -2 (Hurda)</p>
                                <Button
                                    type="danger"
                                    onClick={() => executeLastikOperation({})}
                                    style={{ marginTop: 16 }}
                                >
                                    Hurdaya Taşı
                                </Button>
                            </div>
                        )}
                    </div>
                </Modal>
            )}

            {/* Form Modal */}
            {formModalOpen && (
                <Modal
                    title={modalTitle}
                    open={formModalOpen}
                    onCancel={closeFormModal}
                    footer={null}
                    destroyOnHidden={true}
                    width={800}
                >
                    <div style={{ maxHeight: 600, overflowY: 'auto' }}>
                        <FormikForm
                            formId={currentFormId}
                            uniqueValue={currentUniqueValue}
                            uniqueField={formUniqueField}
                            tableName={formTableName}
                            onSuccess={handleFormSuccess}
                        />
                    </div>
                </Modal>
            )}

            {/* Detail Modal */}
            {detailModalOpen && (
                <Modal
                    title={modalTitle}
                    open={detailModalOpen}
                    onCancel={closeDetailModal}
                    footer={null}
                    destroyOnHidden={true}
                    width={1200}
                    style={{ top: 20 }}
                >
                    <div style={{ maxHeight: 600, overflowY: 'auto' }}>
                        {detailError && (
                            <Alert
                                message="Hata"
                                description={detailError}
                                type="error"
                                showIcon
                                style={{ marginBottom: 16 }}
                            />
                        )}

                        {modalButtonType === 'sub-table' && detailData.length > 0 && detailData[0].root ? (
                            <Table
                                dataSource={detailData}
                                loading={detailLoading}
                                rowKey={(record) => record.dosyaID || record.id || Math.random()}
                                scroll={{ x: 'max-content' }}
                                className={darkMode ? 'dark-table' : ''}
                                size="small"
                                style={{
                                    width: '100%',
                                    backgroundColor: darkMode ? 'var(--dark-bg-secondary)' : undefined
                                }}
                                pagination={{
                                    pageSize: 10,
                                    showSizeChanger: true,
                                    showQuickJumper: true,
                                    showTotal: (total, range) =>
                                        `${range[0]}-${range[1]} / ${total} kayıt`,
                                    pageSizeOptions: ['10', '25', '50', '100']
                                }}
                            >
                                <Table.Column
                                    title="Plaka"
                                    dataIndex="Plaka"
                                    width={120}
                                    ellipsis={{ showTitle: true }}
                                />
                                <Table.Column
                                    title="Belge Tarihi"
                                    dataIndex="Belge Tarihi"
                                    width={120}
                                    ellipsis={{ showTitle: true }}
                                    render={(text) => formatCellValue('Belge Tarihi', text)}
                                />
                                <Table.Column
                                    title="Açıklama"
                                    dataIndex="Açıklama"
                                    width={200}
                                    ellipsis={{ showTitle: true }}
                                />
                                <Table.Column
                                    title="İşlem"
                                    width={200}
                                    render={(text, record) => (
                                        <Space size="small">
                                            <Button
                                                type="default"
                                                size="small"
                                                icon={<EditOutlined />}
                                                onClick={() => handleSubEditFile(record)}
                                                className={darkMode ? 'dark-button' : ''}
                                                style={{
                                                    backgroundColor: darkMode ? 'var(--dark-bg-header)' : undefined,
                                                    borderColor: darkMode ? 'var(--dark-border)' : undefined,
                                                    color: darkMode ? 'var(--dark-text)' : undefined
                                                }}
                                            >
                                                Düzenle
                                            </Button>
                                            <Button
                                                type="default"
                                                size="small"
                                                icon={<EyeOutlined />}
                                                onClick={() => handleViewFile(record)}
                                                className={darkMode ? 'dark-button' : ''}
                                                style={{
                                                    backgroundColor: darkMode ? 'var(--dark-bg-header)' : undefined,
                                                    borderColor: darkMode ? 'var(--dark-border)' : undefined,
                                                    color: darkMode ? 'var(--dark-text)' : undefined
                                                }}
                                            >
                                                Dosyayı Aç
                                            </Button>
                                        </Space>
                                    )}
                                />
                            </Table>
                        ) : (
                            <Table
                                dataSource={detailData}
                                loading={detailLoading}
                                rowKey={(record) => record.dosyaID || record.id || Math.random()}
                                scroll={{ x: 'max-content' }}
                                className={darkMode ? 'dark-table' : ''}
                                size="small"
                                style={{
                                    width: '100%',
                                    backgroundColor: darkMode ? 'var(--dark-bg-secondary)' : undefined
                                }}
                                pagination={{
                                    pageSize: 10,
                                    showSizeChanger: true,
                                    showQuickJumper: true,
                                    showTotal: (total, range) =>
                                        `${range[0]}-${range[1]} / ${total} kayıt`,
                                    pageSizeOptions: ['10', '25', '50', '100']
                                }}
                            >
                                {detailData && detailData.length > 0 && Object.keys(detailData[0]).map((key, index) => {
                                    const getColumnWidth = (key) => {
                                        const keyLength = key.length;
                                        if (key.toLowerCase().includes('tarih') || key.toLowerCase().includes('tar')) {
                                            return Math.max(120, keyLength * 8);
                                        }
                                        if (key.toLowerCase().includes('tutar') || key.toLowerCase().includes('fiyat') || key.toLowerCase().includes('ücret')) {
                                            return Math.max(120, keyLength * 8);
                                        }
                                        if (keyLength > 20) {
                                            return Math.max(200, keyLength * 8);
                                        }
                                        if (keyLength > 10) {
                                            return Math.max(120, keyLength * 8);
                                        }
                                        return Math.max(80, keyLength * 8);
                                    };

                                    return (
                                        <Table.Column
                                            key={key}
                                            title={key}
                                            dataIndex={key}
                                            width={getColumnWidth(key)}
                                            ellipsis={{
                                                showTitle: true
                                            }}
                                            render={(text, record) => (
                                                <span>{formatCellValue(key, text)}</span>
                                            )}
                                            sorter={(a, b) => {
                                                const aVal = a[key];
                                                const bVal = b[key];
                                                if (!isNaN(aVal) && !isNaN(bVal)) {
                                                    return Number(aVal) - Number(bVal);
                                                }
                                                return String(aVal).localeCompare(String(bVal), 'tr');
                                            }}
                                        />
                                    );
                                })}
                            </Table>
                        )}
                    </div>
                </Modal>
            )}

            {/* Tire Detail Modal */}
            {tireDetailModal && (
                <Modal
                    title="Lastik Detay"
                    open={tireDetailModal}
                    onCancel={closeTireDetailModal}
                    footer={null}
                    destroyOnHidden={true}
                    width={1200}
                    style={{ top: 20 }}
                >
                    <div style={{ maxHeight: 600, overflowY: 'auto' }}>
                        {tireDetailError && (
                            <Alert
                                message="Hata"
                                description={tireDetailError}
                                type="error"
                                showIcon
                                style={{ marginBottom: 16 }}
                            />
                        )}

                        {tireDetailLoading ? (
                            <div style={{ textAlign: 'center', padding: 40 }}>
                                <Spin size="large" />
                            </div>
                        ) : tireDetailData.length > 0 ? (
                            <Table
                                dataSource={tireDetailData}
                                loading={tireDetailLoading}
                                rowKey={(record) => record['Lastik No'] || record.id || Math.random()}
                                scroll={{ x: 'max-content' }}
                                className={darkMode ? 'dark-table' : ''}
                                size="small"
                                style={{
                                    width: '100%',
                                    backgroundColor: darkMode ? 'var(--dark-bg-secondary)' : undefined
                                }}
                                pagination={{
                                    pageSize: 10,
                                    showSizeChanger: true,
                                    showQuickJumper: true,
                                    showTotal: (total, range) =>
                                        `${range[0]}-${range[1]} / ${total} kayıt`,
                                    pageSizeOptions: ['10', '25', '50', '100']
                                }}
                            >
                                {tireDetailData && tireDetailData.length > 0 && Object.keys(tireDetailData[0]).map((key, index) => {
                                    const getColumnWidth = (key) => {
                                        const keyLength = key.length;
                                        if (key.toLowerCase().includes('tarih') || key.toLowerCase().includes('tar')) {
                                            return Math.max(120, keyLength * 8);
                                        }
                                        if (key.toLowerCase().includes('tutar') || key.toLowerCase().includes('fiyat') || key.toLowerCase().includes('ücret')) {
                                            return Math.max(120, keyLength * 8);
                                        }
                                        if (keyLength > 20) {
                                            return Math.max(200, keyLength * 8);
                                        }
                                        if (keyLength > 10) {
                                            return Math.max(120, keyLength * 8);
                                        }
                                        return Math.max(80, keyLength * 8);
                                    };

                                    return (
                                        <Table.Column
                                            key={key}
                                            title={key}
                                            dataIndex={key}
                                            width={getColumnWidth(key)}
                                            ellipsis={{
                                                showTitle: true
                                            }}
                                            render={(text, record) => (
                                                <span>{formatCellValue(key, text)}</span>
                                            )}
                                            sorter={(a, b) => {
                                                const aVal = a[key];
                                                const bVal = b[key];
                                                if (!isNaN(aVal) && !isNaN(bVal)) {
                                                    return Number(aVal) - Number(bVal);
                                                }
                                                return String(aVal).localeCompare(String(bVal), 'tr');
                                            }}
                                        />
                                    );
                                })}
                            </Table>
                        ) : (
                            <div style={{
                                textAlign: 'center',
                                padding: 40,
                                color: darkMode ? 'var(--dark-text-muted)' : '#999'
                            }}>
                                Lastik detayı bulunamadı.
                            </div>
                        )}
                    </div>
                </Modal>
            )}
        </div>
    );
};

export default AracDetayPage;